import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { get } from "lodash";
import { Link } from "react-router-dom";
import { useTimeout } from "@sonder/ui/source/hooks/useTimeout";
import { Checkmark, Gear, X } from "@sonder/ui/source/icons";
import { ToastMessage } from "@sonder/ui/source/styled/components/ToastMessage";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import {
  ErrorList,
  ErrorListItem,
} from "@sonder/ui/source/styled/fragments/Error";
import { LinkBaseStyles } from "@sonder/ui/source/styled/fragments/Link";
import { Toast } from "@sonder/ui/source/styled/fragments/Toast";
import { useSegment } from "analytics/segment";
import { FIVE_SECONDS, THIRTY_SECONDS } from "core/constants";
import {
  GetBatchJobErrors,
  useGetBatchJobQuery,
} from "core/queries/GetBatchJob";
import { refetchQueries, removeBatchJob } from "tasks/actions";
import { BatchJob } from "tasks/reducer";
import { useStore } from "tasks/store";
import { BatchJobStatus } from "typings/globalGQLTypes";

const StyledLink = styled(Link)`
  ${LinkBaseStyles}
`;

interface BatchingToastProps extends BatchJob {}

export const BatchingToast = ({ jobId, jobType }: BatchingToastProps) => {
  const { dispatch } = useStore();
  const { data, loading } = useGetBatchJobQuery({
    pollInterval: FIVE_SECONDS,
    variables: { id: jobId },
  });

  const [isVisible, setIsVisible] = useState(false);
  const { track } = useSegment();

  const batchJob = get(data, "batchJob");
  const { errors, total, remaining, status } = batchJob || {
    errors: [] as GetBatchJobErrors[],
    total: 0,
    remaining: 0,
    status: BatchJobStatus.pending,
  };
  const isCompleted = status === BatchJobStatus.complete;
  const hasErrors = isCompleted && (errors && errors.length) > 0;
  // Use state to set visible when batchJob id changes
  useEffect(() => setIsVisible(true), [loading, batchJob && batchJob.id]);
  // Wait 30 seconds before hiding the batch toast if complete and no errors.
  const { start } = useTimeout(
    () => jobId && dispatch(removeBatchJob(jobId)),
    THIRTY_SECONDS,
  );
  useEffect(() => {
    dispatch(refetchQueries(["GetTasks", "GetTask"]));
    if (isCompleted && !hasErrors) start();
  }, [isCompleted, hasErrors]);
  if ((loading && !batchJob) || !isVisible || !batchJob) return null;

  return (
    <Toast>
      {isCompleted ? <Checkmark /> : <Gear />}
      <ToastMessage
        errors={errors}
        jobType={jobType}
        remaining={remaining}
        total={total}
      >
        <ErrorList>
          {errors.map(error => {
            const { task } = error;
            if (!task) return null;

            const taskId = task.id.split("|").pop();
            return (
              <ErrorListItem key={taskId}>
                <StyledLink to={`/tasks/${taskId}`}>{task.name}</StyledLink>
              </ErrorListItem>
            );
          })}
        </ErrorList>
      </ToastMessage>
      <CircleButton
        backgroundVariant="dark"
        onClick={() => {
          dispatch(removeBatchJob(jobId));
          track({
            eventName: "batching_toast",
            element: "dismiss_button",
            action: "click",
            props: {},
          });
        }}
      >
        <X />
      </CircleButton>
    </Toast>
  );
};
